import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import type { CSSProperties } from "react";

type RenameActionModalProps = {
    show: boolean;
    title: string;
    initialValue: string;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: (nextName: string) => void;
    onClose: () => void;
    isLoading?: boolean;
    className?: string;
    backdropClassName?: string;
    style?: CSSProperties;
};

export default function RenameActionModal({
    show,
    title,
    initialValue,
    confirmLabel = "Save",
    cancelLabel = "Cancel",
    onConfirm,
    onClose,
    isLoading = false,
    className,
    backdropClassName,
    style,
}: RenameActionModalProps) {
    const [name, setName] = useState(initialValue);

    useEffect(() => {
        if (show) setName(initialValue);
    }, [show, initialValue]);

    const trimmedName = name.trim();
    const isUnchanged = useMemo(
        () => trimmedName === initialValue.trim(),
        [trimmedName, initialValue],
    );
    const isSubmitDisabled = isLoading || !trimmedName || isUnchanged;

    return (
        <Modal
            centered
            show={show}
            onHide={isLoading ? undefined : onClose}
            backdrop={isLoading ? "static" : true}
            keyboard={!isLoading}
            className={className}
            backdropClassName={backdropClassName}
            style={style}
        >
            <Modal.Header closeButton={!isLoading}>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Form
                onSubmit={(event) => {
                    event.preventDefault();
                    if (isSubmitDisabled) return;
                    onConfirm(trimmedName);
                }}
            >
                <Modal.Body>
                    <Form.Control
                        autoFocus
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder="Enter a name"
                        disabled={isLoading}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                        {cancelLabel}
                    </Button>
                    <Button variant="primary" type="submit" disabled={isSubmitDisabled}>
                        {isLoading ? "Saving..." : confirmLabel}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
